import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import IndexStrip from "../components/IndexStrip";
import Seo from "../components/Seo";
import WinsTickerStrip from "../components/WinsTickerStrip";
import AuthTicker from "../components/auth/AuthTicker";
import AuthHero from "../components/auth/AuthHero";
import AuthForm from "../components/auth/AuthForm";
import TelegramCTA from "../components/TelegramCTA";
import PerformanceDashboard from "../components/PerformanceDashboard";
import LandingShowcase from "../components/LandingShowcase";

export default function AuthPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { login, register, formatApiError } = useAuth();
  const { theme, toggle } = useTheme();
  const initialRef = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("ref")) || "";
  const initialMode = (typeof window !== "undefined" && window.location.pathname === "/register") || initialRef ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [universeTotal, setUniverseTotal] = useState(null);

  // Sync `mode` whenever the route changes (e.g. when the "Start 5-day trial"
  // CTA navigates from /login → /register without unmounting the page).
  // Only scroll on actual pathname *transitions*, never on initial mount —
  // we don't want the AuthForm anchor to capture focus and hide the header.
  const prevPathRef = React.useRef(null);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev == null || prev === location.pathname) {
      // First mount, or same path (strict-mode double-invoke): do nothing.
      return;
    }
    if (location.pathname === "/register") setMode("signup");
    else if (location.pathname === "/login") setMode("login");
    setErr("");
    if (typeof window !== "undefined") {
      const anchor = document.getElementById("auth-form-anchor");
      if (anchor) {
        setTimeout(() => anchor.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    api.get("/universe/count").then(({ data }) => {
      if (!cancelled) setUniverseTotal(data.total);
    }).catch(() => { /* leave null — header just hides count */ });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async ({ email, password, name, referralCode }) => {
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || email.split("@")[0], referralCode || undefined);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full bg-app grain relative overflow-hidden" data-testid="auth-page">
      <Seo
        title={mode === "signup"
          ? "Create account — Breakout Scanner"
          : "Sign in — Breakout Scanner"}
        description="Find high-probability breakout stocks before they explode. Institutional-grade breakout / multi-year breakout NSE signals with precise Entry / Stop-Loss / Target."
        path={mode === "signup" ? "/register" : "/login"}
      />
      {/* BG decorative grid + gradient orbs */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          color: "hsl(var(--text-primary))",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, hsl(160 84% 39%), transparent)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, hsl(38 92% 50%), transparent)" }}
      />

      <button
        type="button"
        onClick={toggle}
        data-testid="theme-toggle-auth"
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="absolute top-4 right-4 z-20 p-2 border border-subtle rounded-sm bg-surface/70 backdrop-blur hover:bg-surface-hover text-muted-fg hover:text-primary-fg transition"
      >
        {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
      </button>

      <div className="relative z-10 flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-6 lg:px-12 py-5 border-b border-subtle bg-app/60 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Logo size={48} />
            <div className="flex items-baseline gap-1.5 font-mono text-base tracking-[0.22em] uppercase">
              <span className="text-primary-fg font-semibold">Breakout</span>
              <span className="text-pos font-semibold">Scanner</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <TelegramCTA variant="pill" testId="header-telegram-cta" />
            <Link to="/wins" className="hover:text-pos transition" data-testid="header-wins-link">
              <span className="text-pos">★</span> Track Record
            </Link>
            <Link to="/blog" className="hover:text-pos transition" data-testid="header-blog-link">
              Blog
            </Link>
            <span data-testid="header-universe-count">
              <span className="text-primary-fg">NSE</span> ·{" "}
              {universeTotal != null ? universeTotal.toLocaleString("en-IN") : "—"} stocks
            </span>
            <span><span className="text-primary-fg">6M / 3Y</span> timeframes</span>
            <span><span className="text-pos">Live</span> scanner</span>
          </div>
        </header>

        <IndexStrip />
        <AuthTicker />
        <WinsTickerStrip />

        {/* Live track-record dashboard — sits above the login form so
            visitors see real scanner performance + NIFTY comparison
            before deciding whether to sign up. */}
        <div className="px-4 sm:px-6 lg:px-10 pt-6 space-y-5">
          <PerformanceDashboard anonymous />
          <LandingShowcase />
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.15fr,1fr]" id="auth-form-anchor">
          <AuthHero />
          <AuthForm
            mode={mode}
            setMode={setMode}
            onSubmit={handleSubmit}
            busy={busy}
            err={err}
            googleLogin={googleLogin}
            initialRef={initialRef}
          />
        </div>

        <footer className="px-6 lg:px-12 py-4 border-t border-subtle text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-wrap items-center gap-4">
          <span>© Breakout Scanner · Educational research · Not SEBI-registered</span>
          <span className="text-primary-fg/40">|</span>
          <Link to="/terms" className="hover:text-primary-fg" data-testid="footer-terms">Terms</Link>
          <Link to="/refund" className="hover:text-primary-fg" data-testid="footer-refund">Refund</Link>
          <Link to="/privacy" className="hover:text-primary-fg" data-testid="footer-privacy">Privacy</Link>
          <Link to="/about" className="hover:text-primary-fg" data-testid="footer-about">About</Link>
          <Link to="/blog" className="hover:text-primary-fg" data-testid="footer-blog">Blog</Link>
          <TelegramCTA variant="footer" testId="auth-footer-telegram" />
          <span className="ml-auto">v1 · powered by yfinance + Gemini 3</span>
        </footer>
      </div>
    </div>
  );
}
